/* eslint-disable prettier/prettier */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray, Form, Formik } from 'formik';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { object, string, array, bool } from 'yup';
import debounce from 'lodash/debounce';
import { omit, isEmpty, get } from 'lodash';
import { colors, fontSize } from '../../constants/styleConstants';
import Button from '../Button';
import EditModeCard, { EditModeCardSection } from '../EditModeCard';
import { dateFormats, earliestYearOfMusicalWork, emptySelectValue, noUrlRegex, TP, VISIBILITY } from '../../constants/consts';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import AddAnotherField from '../AddAnotherField';
import {
  selectLoadingFieldName,
  selectVenuesOptions,
  selectLibrettistNameOptions,
  selectAddOption,
} from '../../containers/ProductionUpdateSteps/selectors';
// remove to separate container or use app actions for the case when this modal used not in production flow
import {
  cleanOptionsValuesAction,
  getVenuesAction,
  getLibrettistsNamesAction,
} from '../../containers/ProductionUpdateSteps/actions';
import Title from '../Title';
import FormikTextField from '../Fields/TextField';
import CalendarField from '../Fields/CalendarField';
import SelectField from '../Fields/SelectField';
import {
  cleanAggregationsAction,
  createEntity,
  getAggregationsAction,
  getList,
  getArtistVoiceTypesAction,
  getWorkStylesAction,
  getWorkTypesAction,
  getCitiesAction,
  useBufferValueAction,
  getCompaniesAction,
} from '../../containers/App/actions';
import {
  COUNTRY_CITY_ENDPOINT,
  MUSICAL_WORK_ENDPOINT,
  MUSICAL_WORK_LIBRETTIST_ENDPOINT,
  MUSICAL_WORK_STYLES_ENDPOINT,
  MUSICAL_WORK_TYPES_ENDPOINT,
  PROFESSION_QUALIFICATIONS_ENDPOINT,
  VENUES,
} from '../../constants/endpointConstants';
import {
  selectAggregationsLoading,
  selectAggregationsOptions,
  selectBufferValueData,
  selectCitiesOptions,
  selectCompaniesListOptions,
  selectCompaniesListOptionsLoading,
  selectLanguagesOptions,
  selectVoiceTypesOptions,
  selectWorkStylesOptions,
  selectWorkTypesOptions,
} from '../../containers/App/selectors';
import { cardFields } from '../../containers/ProductionUpdateSteps/constants';
import ActionStatusModal from '../ActionStatusModal';
import TextButton from '../TextButton';
import TimePicker from '../Fields/TimePicker';
import SubmitModalLayout from '../SubmitModalLayout';
import { musicalWork } from '../../constants/formFields';
import FormikCheckbox from '../Fields/CheckboxField/FormikCheckbox';
import VenuesFieldsGroup from '../EditComponents/VenuesFieldsGroup';
import { getIdObjectOrNull, getOption } from '../../utils/common';
import FormikOnError from '../Fields/FormikOnError';
import TextArea from '../Fields/TextArea';
import TitleWithInfo from "../TitleWithInfo";
import AddNewCompanyModal from '../AddNewCompanyModal';
import NoOptionsBox from '../NoOptionsBox';
import { createDate } from "../../utils/date";
import { isFocusedAddAnotherField } from '../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
    marginBottom: 150,
  },
  fieldLabel: {
    marginTop: '15px',
    fontSize: fontSize.medium,
    color: colors.cancelButton,
  },
  selectIcon: {
    color: colors.orange,
  },
  errorText: {
    position: 'absolute',
    bottom: -16,
  },
}));

const {
  composerField,
  workField,
  aliasField,
  librettists,
  librettoLanguage,
  librettoDescription,
  workType,
  musicStyle,
  workAs,
  summary,
  premiereDate,
  premiereTime,
  company,
  roleNameField,
  voiceType,
  areRolesUnknown,
} = musicalWork;

const AddNewWorkModal = ({
  composerNames,
  librettistNames,
  librettoLanguages,
  workTypes,
  musicStyles,
  companyNames,
  voiceTypes,
  optionsLoading,
  loadingFieldName,
  onClose,
  composerValue,
  workValue,
  citiesOptions,
  addOption,
  venuesOptions,
  createdWork,
  companiesLoading,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef();

  const [confirmModalOpened, setConfirmModalOpened] = React.useState(false);
  const [editableFieldName, setEditableFieldName] = React.useState(null);
  const [isAddNewCompanyModalOpened, setAddNewCompanyModalOpened] = React.useState(false);
  const [newCompanyName, setNewCompanyName] = React.useState('');

  let allLibrettists = [];

  React.useEffect(() => {
    if (!workTypes.length) {
      dispatch(
        getList({
          endpoint: MUSICAL_WORK_TYPES_ENDPOINT,
          sagaRoutine: getWorkTypesAction,
          params: { queryParams: {} },
        }),
      );
    }
    if (!musicStyles.length) {
      dispatch(
        getList({
          endpoint: MUSICAL_WORK_STYLES_ENDPOINT,
          sagaRoutine: getWorkStylesAction,
          params: { queryParams: {} },
        }),
      );
    }
    if (!voiceTypes.length) {
      dispatch(
        getList({
          endpoint: PROFESSION_QUALIFICATIONS_ENDPOINT,
          sagaRoutine: getArtistVoiceTypesAction,
          params: { queryParams: {} },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchVenue = debounce((fieldName, value, queryParams) => {
    dispatch(
      getList({
        endpoint: VENUES,
        sagaRoutine: getVenuesAction,
        params: { fieldName, queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL, ...queryParams } },
      }),
    );
  }, 300);

  const searchAggregations = debounce((fieldName, value, countryId) => {
    dispatch(
      getList({
        endpoint: COUNTRY_CITY_ENDPOINT(countryId),
        sagaRoutine: getCitiesAction,
        fieldName,
        params: {
          queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL },
        },
      }),
    );
  }, 300);

  const omitEmptyField = obj => {
    const omittedFields = Object.keys(obj).filter(el => {
      if (typeof obj[el] === 'string') {
        return !obj[el].length;
      }
      if (typeof obj[el] === 'boolean') {
        return !obj[el];
      }
      if (el === 'premiere') {
        const test = omitEmptyField(obj[el]);
        // eslint-disable-next-line no-param-reassign
        obj[el] = test;
        return isEmpty(test);
      }
      return (!obj[el]?.id && el !== 'roles' && el !== librettists.name) || (el === librettists.name && isEmpty(obj[el]));
    });
    return omit(obj, omittedFields);
  };

  const postWork = work => {
    dispatch(
      createEntity({
        endpoint: MUSICAL_WORK_ENDPOINT,
        sagaRoutine: useBufferValueAction,
        params: omitEmptyField(work),
        callback: onClose,
      }),
    );
  };

  const clearValues = type => {
    switch (type) {
      case cardFields.city.name:
        dispatch(cleanAggregationsAction(cardFields.city.name));
        break;
      case cardFields.venue.name:
        dispatch(cleanOptionsValuesAction('venues'));
        break;
      default:
        break;
    }
  };

  const commonVenuesFieldValues = {
    searchVenue,
    searchCities: searchAggregations,
    loadingValue: loadingFieldName,
    venues: venuesOptions,
    cities: citiesOptions?.options,
    clearValues,
  };

  const getInitialFormValue = () =>
    addOption.tempFormState || {
      [composerField.name]: composerValue,
      [workField.name]: workValue,
      [aliasField.name]: '',
      [librettists.name]: [undefined],
      [librettoLanguage.name]: '',
      [librettoDescription.name]: '',
      [workType.name]: '',
      [musicStyle.name]: '',
      [workAs.name]: emptySelectValue,
      [summary.name]: '',
      [premiereDate.name]: '',
      [premiereTime.name]: '',
      [company.name]: '',
      venue: {},
      [areRolesUnknown.name]: false,
      roles: [{ [roleNameField?.name]: '', [voiceType?.name]: '' }],
    };

  const searchComposers = query => {
    dispatch(
      getAggregationsAction({
        fieldName: composerField.name,
        query: {
          aggregation_type: 'composer_id',
          aggregation_query: query,
          show_invalid: true,
          visibility: VISIBILITY.ALL,
        },
      }),
    );
  };

  const searchLibrettistByName = debounce(query => {
    dispatch(
      getList({
        endpoint: MUSICAL_WORK_LIBRETTIST_ENDPOINT,
        sagaRoutine: getLibrettistsNamesAction,
        params: { queryParams: { query, show_invalid: true, visibility: VISIBILITY.ALL } },
      }),
    );
  }, 300);

  const searchCompanyNames = query => {
    dispatch(getCompaniesAction({ query: { query, show_invalid: true, visibility: VISIBILITY.ALL } }));
  };

  const getLibrettistsValue = (names, description) => {
    const signValues = names.filter(el => el);
    // eslint-disable-next-line no-underscore-dangle
    let valuesToSend = signValues.length ? signValues.map(el => el?.__isNew__
      ? { librettist: { name: el?.value } }
      : { librettist: { id: el?.value } })
      : null
    // the field 'description' should be the same for all the workLibrettists
    if (description) {
      valuesToSend = valuesToSend?.map(el => ({ ...el, description }));
    }
    return valuesToSend;
  };

  const formatRequestBody = formObj => ({
    composer: { id: formObj[composerField.name].value },
    name: formObj[workField.name],
    workType: getIdObjectOrNull(get(formObj, `${workType.name}.value`)),
    ...(formObj[areRolesUnknown.name]
      ? {}
      : {
        roles: formObj?.roles?.reduce((acc, el) => {
          const role = get(el, roleNameField?.name) && {
            name: get(el, roleNameField?.name),
            profession: { id: 1 },
            professionQualification: getIdObjectOrNull(get(el, `${voiceType.name}.value`)),
          };
          if (role) return [...acc, role];
          return acc;
        }, []),
      }),
    alias: formObj[aliasField.name], // eslint-disable-next-line no-underscore-dangle,no-nested-ternary
    workLibrettists: getLibrettistsValue(formObj[librettists.name], formObj[librettoDescription.name]),
    librettoLanguage: getIdObjectOrNull(get(formObj, `${librettoLanguage.name}.value`)),
    musicStyle: getIdObjectOrNull(get(formObj, `${musicStyle.name}.value`)),
    is_brief: formObj[workAs.name].value === 'brief',
    is_children: formObj[workAs.name].value === 'children',
    is_rare: formObj[workAs.name].value === 'rare',
    summary: formObj[summary.name],
    premiere: {
      date: formObj[premiereDate.name],
      time: formObj[premiereTime.name],
      venue: getIdObjectOrNull(formObj.venue?.venue_id?.value),
      company: getIdObjectOrNull(formObj[company.name]?.value),
    },
  });

  const confirmAddNewWork = () => {
    setConfirmModalOpened(false);
    postWork(formatRequestBody(formRef.current.values));
  };

  const addNewWorkSchema = object().shape({
    [composerField.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [workField.name]: string()
      .trim()
      .nullable()
      .min(2, `${TP}.FN_MIN_LENGTH_2`)
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [librettists.name]: array()
      .test('all-librettists', '', function (value) {
        allLibrettists = value;
        return true;
      })
      .of(object()
        .shape({
          label: string().test('duplicate', t(`${TP}.FN_DUPLICATED_VALUE`), function (value) {
            return (!value || allLibrettists.filter(el => el?.label && el?.label.toLowerCase().trim() === value.toLowerCase().trim(),).length === 1);
          }),
        })
        .nullable()),
    [summary.name]: string().matches(noUrlRegex, `${TP}.FN_LINKS_VALIDATION_MESSAGE`),
    [workType.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [areRolesUnknown.name]: bool(),
    roles: array().when(areRolesUnknown.name, (isUnknown, schema) => {
      if (!isUnknown) {
        return schema.of(object().shape({
          [roleNameField.name]: string().required(`${TP}.FN_REQUIRED_FIELD`),
        }))
      }
      return schema;
    }),
    [premiereTime.name]: string()
      .trim()
      .nullable()
      .length(5, `${TP}.FN_INVALID_TIME`),
  });

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_WORK`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => { })();
      }}
      isLoading={createdWork?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={getInitialFormValue()}
          onSubmit={(values, { setSubmitting }) => {
            setConfirmModalOpened(true);
            setSubmitting(false);
          }}
          enableReinitialize
          validationSchema={addNewWorkSchema}
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <FormikOnError withFocus>
                <EditModeCardSection first>
                  <Title>{t(`${TP}.FN_SELECT_COMPOSER_NAME`)}</Title>
                  <Grid container spacing={4}>
                    <Grid item lg={5} sm={6} xs={12}>
                      <Field
                        name={composerField.name}
                        component={FormikAutocompleteSelect}
                        instanceId={composerField.name}
                        options={composerNames}
                        minSearchCharsLength={3}
                        onInputTextChange={v => searchComposers(v)}
                        isLoading={optionsLoading === 'composer_id'}
                        isClearable
                        label={t(`${TP}.m_COMPOSER`)}
                        placeholder={t(composerField.placeholder)}
                        isDisabled
                      />
                    </Grid>
                  </Grid>
                </EditModeCardSection>
                <EditModeCardSection>
                  <Title>{t(`${TP}.FN_ENTER_MUSICAL_WORK`)}</Title>
                  <Grid container spacing={4}>
                    <Grid item lg={5} sm={6} xs={12}>
                      <Field
                        name={workField.name}
                        component={FormikTextField}
                        t={t}
                        label={t(workField.label)}
                        placeholder={t(workField.placeholder)}
                        nativeInputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item lg={4} sm={6} xs={12}>
                      <Field
                        name={aliasField.name}
                        component={FormikTextField}
                        t={t}
                        placeholder={t(aliasField.placeholder)}
                        label={t(aliasField.label)}
                        nativeInputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                  </Grid>
                </EditModeCardSection>
                <EditModeCardSection>
                  <Title>{t(librettists.title)}</Title>
                  <Grid container spacing={4}>
                    <Grid item lg={5} sm={6} xs={12}>
                      <FieldArray
                        name={librettists.name}
                        render={arrayHelpers =>
                          values[librettists.name].map((el, index) => {
                            const fieldName = `${librettists.name}.${index}`
                            return (
                              <AddAnotherField
                                key={fieldName}
                                removeFunc={() => arrayHelpers.remove(index)}
                                addFunc={() => arrayHelpers.push(null)}
                                isLast={index + 1 === values[librettists.name].length}
                                values={values?.[librettists.name]}
                                addDisabled={values[librettists.name].some(v => !v)}
                              >
                                <Field
                                  name={fieldName}
                                  component={FormikAutocompleteSelect}
                                  instanceId={fieldName}
                                  options={librettistNames}
                                  minSearchCharsLength={3}
                                  onInputTextChange={searchLibrettistByName}
                                  isLoading={editableFieldName === fieldName && loadingFieldName === 'librettists'}
                                  isClearable
                                  label={t(librettists.label)}
                                  isCreatable
                                  onMenuOpen={() => setEditableFieldName(fieldName)}
                                />
                              </AddAnotherField>
                            );
                          })
                        }
                      />
                    </Grid>
                    <Grid item lg={4} sm={6} xs={12}>
                      <Field
                        name={librettoLanguage.name}
                        component={FormikAutocompleteSelect}
                        instanceId={librettoLanguage.name}
                        options={librettoLanguages}
                        isClearable
                        placeholder={t(librettoLanguage.placeholder)}
                        label={t(librettoLanguage.label)}
                        filterOption={({ label }, query) =>
                          typeof label === 'string' && label.toLowerCase().startsWith(query.toLowerCase())
                        }
                      />
                    </Grid>
                  </Grid>
                  <div style={{ marginTop: 16 }}>
                    <TitleWithInfo title={librettoDescription.label} tooltip={`${TP}.FN_LIBRETTO_DESCRIPTION`} titleSize="medium" />
                    <Field
                      name={librettoDescription.name}
                      component={TextArea}
                      minLines={10}
                    />
                  </div>
                </EditModeCardSection>
                <EditModeCardSection>
                  <Title>{t(workType.title)}</Title>
                  <Grid container spacing={4}>
                    <Grid item lg={3} sm={4} xs={12}>
                      <Field
                        name={workType.name}
                        component={FormikAutocompleteSelect}
                        instanceId={workType.name}
                        options={workTypes}
                        isClearable
                        placeholder={t(workType.placeholder)}
                        label={t(workType.label)}
                      />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                      <Field
                        name={musicStyle.name}
                        component={FormikAutocompleteSelect}
                        instanceId={musicStyle.name}
                        options={musicStyles}
                        isClearable
                        placeholder={t(musicStyle.placeholder)}
                        label={t(musicStyle.label)}
                      />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                      <Field
                        name={workAs.name}
                        component={SelectField}
                        options={[
                          { label: t(`${TP}.FN_BRIEF_OPTION`), value: 'brief' },
                          { label: t(`${TP}.FN_CHILDREN_OPTION`), value: 'children' },
                          { label: t(`${TP}.FN_RARE_OPTION`), value: 'rare' },
                        ]}
                        placeholder={t(workAs.placeholder)}
                        label={t(workAs.label)}
                        staticLabel
                        onChange={() => { }}
                        iconType="dropdown"
                        iconclass={classes.selectIcon}
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.fieldLabel}>{t(summary.label)}</div>
                  <Field
                    name={summary.name}
                    component={TextArea}
                    minLines={10}
                  />
                </EditModeCardSection>
                <EditModeCardSection>
                  <Title>{t(premiereDate.title)}</Title>
                  <Grid container spacing={4}>
                    <Grid item lg={2} sm={3} xs={6}>
                      <Field
                        name={premiereDate.name}
                        label={t(premiereDate.label)}
                        component={CalendarField}
                        fromMonth={new Date(earliestYearOfMusicalWork, 11)} // earliest year of opera creation
                        placeholder={t(premiereDate.placeholder)}
                        onChange={day => setFieldValue(premiereDate.name, day ? createDate(day).format(dateFormats.basic) : '')}
                      />
                    </Grid>
                    <Grid item lg={2} sm={3} xs={6}>
                      <Field
                        component={TimePicker}
                        label={t(premiereTime.label)}
                        name={premiereTime.name}
                        onTimeChange={time => setFieldValue(premiereTime.name, time)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={4}>
                    <Grid item sm={8} xs={12}>
                      <Field
                        name={company.name}
                        component={FormikAutocompleteSelect}
                        instanceId={company.name}
                        options={companyNames}
                        minSearchCharsLength={3}
                        onInputTextChange={v => {
                          searchCompanyNames(v);
                          setNewCompanyName(v);
                        }}
                        isLoading={companiesLoading}
                        isClearable
                        placeholder={t(company.placeholder)}
                        label={t(company.label)}
                        onChange={c => {
                          if (c?.city) {
                            setFieldValue('venue', {
                              ...values.venue,
                              [cardFields.city.name]: { value: c?.city?.id, label: c?.city?.name },
                            });
                          }
                          if (c?.country) {
                            setFieldValue('venue', {
                              ...values.venue,
                              [cardFields.country.name]: { value: c?.country?.id, label: c?.country?.name },
                            });
                          }
                        }}
                        customComponents={{
                          NoOptionsMessage: () => (
                            <NoOptionsBox
                              buttonText={`${TP}.FN_ADD_NEW_COMPANY`}
                              openFormFunc={() => setAddNewCompanyModalOpened(true)}
                              t={t}
                            />
                          ),
                        }}
                        staticOption={
                          <Button onClick={() => setAddNewCompanyModalOpened(true)}>{t(`${TP}.FN_ADD_NEW_COMPANY`)}</Button>
                        }
                      />
                    </Grid>
                  </Grid>
                  <VenuesFieldsGroup
                    hideStage
                    {...commonVenuesFieldValues}
                    setFieldValue={setFieldValue}
                    parentFieldName="venue"
                    values={values.venue}
                  />
                </EditModeCardSection>
                <EditModeCardSection>
                  <Field name={areRolesUnknown.name} label={t(areRolesUnknown.label)} component={FormikCheckbox} />
                  {!values[areRolesUnknown?.name] && (
                    <>
                      <Title>{t(roleNameField.title)}</Title>
                      <FieldArray
                        name="roles"
                        render={arrayHelpers =>
                          values.roles.map((el, index) => {
                            const roleName = `roles.${index}.${roleNameField?.name}`;
                            const voiceTypeName = `roles.${index}.${voiceType?.name}`;
                            return (
                              <AddAnotherField
                                key={index}
                                removeFunc={() => arrayHelpers.remove(index)}
                                addFunc={() => arrayHelpers.push({ [roleNameField?.name]: '', [voiceType?.name]: '' })}
                                isLast={index + 1 === values?.roles?.length}
                                values={values?.roles}
                                addDisabled={!get(el, roleNameField?.name)}
                              >
                                <Grid container spacing={4}>
                                  <Grid item sm={6} xs={12}>
                                    <Field
                                      autoFocus={isFocusedAddAnotherField(index, values.roles)}
                                      name={roleName}
                                      component={FormikTextField}
                                      t={t}
                                      placeholder={t(roleNameField.placeholder)}
                                      label={!index ? t(roleNameField.label) : ''}
                                      nativeInputProps={{ maxLength: 255 }}
                                      FormHelperTextProps={{ classes: { root: classes.errorText } }}
                                    />
                                  </Grid>
                                  <Grid item sm={6} xs={12}>
                                    <Field
                                      name={voiceTypeName}
                                      component={FormikAutocompleteSelect}
                                      instanceId={voiceTypeName}
                                      options={voiceTypes}
                                      isClearable
                                      placeholder={t(voiceType.placeholder)}
                                      label={!index ? t(voiceType.label) : ''}
                                    />
                                  </Grid>
                                </Grid>
                              </AddAnotherField>
                            );
                          })
                        }
                      />
                    </>
                  )}
                </EditModeCardSection>
              </FormikOnError>
              {isAddNewCompanyModalOpened && (
                <AddNewCompanyModal
                  onClose={createdCompany => {
                    if (createdCompany?.id) {
                      setFieldValue(company.name, getOption(createdCompany));
                    }
                    setAddNewCompanyModalOpened(false);
                    setNewCompanyName(null);
                  }}
                  companyName={newCompanyName || ''}
                />
              )}
            </Form>
          )}
        </Formik>
      </EditModeCard>
      <ActionStatusModal
        isOpened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        buttons={[
          <TextButton onClick={() => setConfirmModalOpened(false)}>{t(`${TP}.m_CANCEL`)}</TextButton>,
          <Button onClick={confirmAddNewWork}>{t(`${TP}.m_CONFIRM`)}</Button>,
        ]}
      >
        <span>{t(`${TP}.FN_NEW_MUSICAL_WORK_CONFIRM_TEXT`)}</span>
      </ActionStatusModal>
    </SubmitModalLayout>
  );
};

AddNewWorkModal.propTypes = {
  composerNames: PropTypes.array,
  librettistNames: PropTypes.array,
  workTypes: PropTypes.array,
  musicStyles: PropTypes.array,
  companyNames: PropTypes.array,
  voiceTypes: PropTypes.array,
  optionsLoading: PropTypes.string,
  loadingFieldName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  composerValue: PropTypes.object,
  workValue: PropTypes.string,
  citiesOptions: PropTypes.shape({
    options: PropTypes.array,
    loading: PropTypes.bool,
  }),
  venuesOptions: PropTypes.array,
  companiesLoading: PropTypes.bool,
};

AddNewWorkModal.defaultProps = {
  composerNames: [],
  librettistNames: [],
  workTypes: [],
  musicStyles: [],
  companyNames: [],
  voiceTypes: [],
  optionsLoading: '',
  loadingFieldName: '',
  composerValue: '',
  workValue: '',
  companiesLoading: false,
};

const mapStateToProps = createStructuredSelector({
  composerNames: selectAggregationsOptions('composer_id'),
  librettistNames: selectLibrettistNameOptions(),
  librettoLanguages: selectLanguagesOptions(),
  workTypes: selectWorkTypesOptions(),
  musicStyles: selectWorkStylesOptions(),
  companyNames: selectCompaniesListOptions(),
  companiesLoading: selectCompaniesListOptionsLoading(),
  voiceTypes: selectVoiceTypesOptions(),
  optionsLoading: selectAggregationsLoading(),
  loadingFieldName: selectLoadingFieldName(),
  citiesOptions: selectCitiesOptions(),
  addOption: selectAddOption(),
  venuesOptions: selectVenuesOptions(),
  createdWork: selectBufferValueData(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(AddNewWorkModal);
